import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { NAME_ATTRIBUTE, __DEPRECATED_RENDER_EVENT, MOUNT_OR_UPDATE_EVENT, UNMOUNT_EVENT } from './constants';
import debounce from 'lodash.debounce';

/**
 * Fallback for Node enviroments.
 */
global.ResizeObserver = global.ResizeObserver || class {
  observe() {}
  disconnect() {}
};
global.requestAnimationFrame = global.requestAnimationFrame || (callback => callback());
function queryElement(name) {
  var _global$document;
  if (!name) return null;
  const element = (_global$document = global.document) === null || _global$document === void 0 ? void 0 : _global$document.querySelector("[".concat(NAME_ATTRIBUTE, "=\"").concat(name, "\"]"));
  if (!element) return null;
  return element;
}
function getData(element) {
  if (!element) return null;
  const {
    top,
    right,
    bottom,
    left,
    width,
    height,
    x,
    y
  } = element.getBoundingClientRect();
  return {
    element,
    top,
    right,
    bottom,
    left,
    width,
    height,
    x,
    y
  };
}
function isEqualData(a, b, attrs) {
  if (a === b) return true;
  if (!a || !b) return a === b;
  return ['element', ...attrs].reduce((acc, key) => acc && a[key] === b[key], true);
}

/**
 * Hook which searches for a given component in the DOM. If this component is found, some resources will be returned,
 * otherwise returns `null`. These resources are updated if the component is found in the future.
 *
 * @param {React.Component} Component - Component which will be searched. Must have `displayName` property.
 * @param {Array.<string>} [listenedAttrs] - List of attributes that will be listened to determinate data update.
 * @returns {[Object, Function]|null} resources - If the component is found, returns an array where the first element is
 * a set of information about the found component, and the second one is a function which redoes the search when executed.
 * Returns `null` if the component is not found.
 * @returns {Object} foo[0] - A set of information about the found component
 * @returns {Function} foo[1] - Function which redoes the search when executed,
 * updating the value of the returned array's first item.
 * @returns {Element} foo[0].element - DOM element associated with the found component.
 * @returns {number} foo[0].top - The top coordinate value of the found component.
 * @returns {number} foo[0].right - The right coordinate value of the found component.
 * @returns {number} foo[0].bottom - The bottom coordinate value of the found component.
 * @returns {number} foo[0].left - The left coordinate value of the found component.
 * @returns {number} foo[0].width - The width of the found component.
 * @returns {number} foo[0].height - The height of the found component.
 * @returns {number} foo[0].x - The x coordinate of the found component.
 * @returns {number} foo[0].y - The y coordinate of the found component.
 */
export default function useCrossAppSearch(Component) {
  let listenedAttrs = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : ['top', 'right', 'bottom', 'left', 'width', 'height', 'x', 'y'];
  const name = typeof Component === 'string' ? Component : Component === null || Component === void 0 ? void 0 : Component.displayName;
  const elementRef = useRef(null);
  const [data, setData] = useState(null);
  const remove = useCallback(() => setData(null), []);
  const reload = useCallback(() => {
    elementRef.current = queryElement(name);
    const newData = getData(elementRef.current);
    !isEqualData(data, newData, listenedAttrs) && setData(newData);
  }, [name, data, listenedAttrs]);
  const resizeObserver = useMemo(() => new global.ResizeObserver(debounce(() => global.requestAnimationFrame(reload), 300)), [reload]);
  useEffect(() => {
    const mountOrUpdateListener = _ref => {
      let {
        detail
      } = _ref;
      return detail.name === name && reload();
    };
    const unmountListener = _ref2 => {
      let {
        detail
      } = _ref2;
      return detail.name === name && remove();
    };
    reload();
    elementRef.current && resizeObserver.observe(elementRef.current);

    // Deprecated
    global.document.addEventListener(__DEPRECATED_RENDER_EVENT, mountOrUpdateListener);
    global.document.addEventListener(MOUNT_OR_UPDATE_EVENT, mountOrUpdateListener);
    global.document.addEventListener(UNMOUNT_EVENT, unmountListener);
    return () => {
      // Deprecated
      global.document.removeEventListener(__DEPRECATED_RENDER_EVENT, mountOrUpdateListener);
      global.document.removeEventListener(MOUNT_OR_UPDATE_EVENT, mountOrUpdateListener);
      global.document.removeEventListener(UNMOUNT_EVENT, unmountListener);
      elementRef.current && resizeObserver.unobserve(elementRef.current);
    };
  }, [name, resizeObserver, reload, remove]);
  return [data, reload];
}